import React, { useCallback, useEffect, useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  Grid,
  Link,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import { OrganizationEndpoints } from "@generated/organization";
import { ProjectEndpoints } from "src/generated/project";
import { PROJECTS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EmptyState } from "src/shell/components/EmptyState";
import { getCardStyles } from "src/shell/styles/themeUtils";

import { useOrgRealtime } from "../../hooks/useOrgRealtime";
import type { Organization } from "../../types";

interface OrgMetricsPanelProps {
  orgAlias: string;
  organization?: Organization;
}

interface OrgSummary {
  memberCount: number;
  projectCount: number;
  systemCount: number;
  runningCount: number;
  failedCount: number;
}

interface ProjectRow {
  alias: string;
  name: string;
  stackCount: number;
}

const EMPTY_SUMMARY: OrgSummary = {
  memberCount: 0,
  projectCount: 0,
  systemCount: 0,
  runningCount: 0,
  failedCount: 0,
};

export function OrgMetricsPanel({
  orgAlias,
  organization,
}: OrgMetricsPanelProps) {
  const theme = useTheme();
  const { billing, billingLoading } = useOrgRealtime(orgAlias);
  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(false);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(false);
    try {
      const response = await fetch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.GET_ORGANIZATION_SUMMARY.replace(":id", encodeURIComponent(orgAlias))}`,
        {
          credentials: "include",
        },
      );
      if (!response.ok) {
        setSummaryError(true);
        return;
      }
      setSummary((await response.json()) as OrgSummary);
    } catch {
      setSummary(null);
      setSummaryError(true);
    } finally {
      setSummaryLoading(false);
    }
  }, [orgAlias]);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(false);
    try {
      const response = await fetch(
        `${apiBaseUrls.BACKEND}${ProjectEndpoints.LIST_PROJECTS}?limit=100&offset=0`,
        {
          credentials: "include",
        },
      );
      if (!response.ok) {
        setProjectsError(true);
        setProjects([]);
        return;
      }
      const projectData = (await response.json()) as {
        projects: {
          alias: string;
          name: string;
          appCount?: number;
        }[];
      };
      setProjects(
        projectData.projects.map((project) => ({
          ...project,
          stackCount: project.appCount ?? 0,
        })),
      );
    } catch {
      setProjects([]);
      setProjectsError(true);
    } finally {
      setProjectsLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchSummary();
    void fetchProjects();
  }, [fetchProjects, fetchSummary]);

  const currentSummary = summary ?? EMPTY_SUMMARY;
  const spendTotals = useMemo(() => {
    const totals = { compute: 0, storage: 0 };
    for (const system of billing?.systems ?? []) {
      for (const item of system.lineItems) {
        const amount = Number.parseFloat(item.total.replace(/^\$/, ""));
        if (!Number.isFinite(amount)) continue;
        if (item.label.toLowerCase().includes("storage")) {
          totals.storage += amount;
        } else {
          totals.compute += amount;
        }
      }
    }
    return totals;
  }, [billing]);
  const spendTotal = spendTotals.compute + spendTotals.storage;

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <KpiCard
          label="Projects"
          value={
            summaryLoading || summaryError ? "—" : currentSummary.projectCount
          }
          detail="Activity trend unavailable"
        />
        <KpiCard
          label="Members"
          value={
            summaryLoading || summaryError ? "—" : currentSummary.memberCount
          }
          detail="Invite trend unavailable"
        />
        <KpiCard
          label="Applications"
          value={
            summaryLoading || summaryError ? "—" : currentSummary.systemCount
          }
          detail={
            summaryLoading || summaryError
              ? "Status unavailable"
              : `${currentSummary.runningCount} running`
          }
        />
        <KpiCard
          label="MTD spend"
          value={
            billingLoading || !billing
              ? "—"
              : `$${Number.parseFloat(billing.totalUsd).toFixed(2)}`
          }
          detail={billing?.plan === "paid" ? "Usage-based" : "Free tier"}
        />
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Card sx={getCardStyles(theme)}>
              <SectionTitle>Organization metadata</SectionTitle>
              <MetadataRow
                label="Name"
                value={organization?.name ?? orgAlias}
              />
              <MetadataRow
                label="Alias"
                value={organization?.alias ?? orgAlias}
                code
              />
              <MetadataRow
                label="Description"
                value={organization?.description || "Not available"}
              />
              <MetadataRow label="Owner" value="Not available" />
              <MetadataRow
                label="Created"
                value={
                  organization
                    ? formatDate(organization.createdAt)
                    : "Not available"
                }
              />
              <MetadataRow label="Region" value="Not available" />
            </Card>

            <Card sx={getCardStyles(theme)}>
              <SectionTitle
                action={
                  <Link
                    component={RouterLink}
                    to={PROJECTS.LIST.getPath(orgAlias)}
                    underline="hover"
                  >
                    View all
                  </Link>
                }
              >
                Projects
              </SectionTitle>
              {projectsLoading ? (
                <LinearProgress />
              ) : projectsError ? (
                <EmptyState
                  icon={InfoOutlinedIcon}
                  title="Projects unavailable"
                  subtitle="Project data could not be loaded. Try refreshing the page."
                />
              ) : projects.length === 0 ? (
                <EmptyState
                  icon={InfoOutlinedIcon}
                  title="No projects yet"
                  subtitle="Create a project to see it here."
                />
              ) : (
                projects.slice(0, 3).map((project) => (
                  <Stack
                    component={RouterLink}
                    to={PROJECTS.DETAILS.getPath(orgAlias, project.alias)}
                    key={project.alias}
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      py: 1.25,
                      color: "inherit",
                      textDecoration: "none",
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{project.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.stackCount} stack
                        {project.stackCount === 1 ? "" : "s"}
                      </Typography>
                    </Box>
                    <Typography color="text.disabled">→</Typography>
                  </Stack>
                ))
              )}
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Card sx={getCardStyles(theme)}>
              <SectionTitle action="Unavailable">Recent activity</SectionTitle>
              <EmptyState
                icon={InfoOutlinedIcon}
                title="Activity data unavailable"
                subtitle="Organization activity will appear here when its activity feed is available."
              />
            </Card>

            <Card sx={getCardStyles(theme)}>
              <SectionTitle>Usage this month</SectionTitle>
              <UsageRow
                label="Compute"
                value={spendTotals.compute}
                total={spendTotal}
                loading={billingLoading}
              />
              <UsageRow
                label="Storage"
                value={spendTotals.storage}
                total={spendTotal}
                loading={billingLoading}
              />
              {!billingLoading && !billing && (
                <Typography variant="body2" color="text.secondary">
                  Usage data unavailable.
                </Typography>
              )}
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

function KpiCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: React.ReactNode;
  detail: string;
}) {
  const theme = useTheme();
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card
        sx={{
          ...getCardStyles(theme),
          p: 2,
          minHeight: 112,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </Card>
    </Grid>
  );
}

function SectionTitle({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 1.5 }}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {children}
      </Typography>
      {action && (
        <Typography variant="caption" color="text.secondary">
          {action}
        </Typography>
      )}
    </Stack>
  );
}

function MetadataRow({
  label,
  value,
  code = false,
}: {
  label: string;
  value: string;
  code?: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={2}
      sx={{ py: 0.75 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component={code ? "code" : "span"}
        sx={{ textAlign: "right", overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function UsageRow({
  label,
  value,
  total,
  loading,
}: {
  label: string;
  value: number;
  total: number;
  loading: boolean;
}) {
  const percent = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {loading ? "—" : `$${value.toFixed(2)}`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={`${label} usage`}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

export default OrgMetricsPanel;
